/**
 * Jalali (Solar Hijri) display helpers. Calendar math (`toJalali`, `fromJalali`,
 * business days, `computeEligibleAt`) comes from `@mfa-aff/shared`, the same code the API
 * uses for holds. Digits are kept Latin in the strings; IRANYekanX `ss02` draws them Persian.
 * Month and weekday names for the extra styles come from Intl with the Persian calendar.
 */
import { JALALI_MONTH_NAMES, addCalendarDays, formatJalali as sharedFormatJalali, toJalali } from "@mfa-aff/shared";

export { JALALI_MONTH_NAMES, toJalali };
export { fromJalali, addBusinessDays, computeEligibleAt, jalaliMonthLength } from "@mfa-aff/shared";

export type DateLike = Date | string | number;

const LOCALE = "fa-IR-u-ca-persian-nu-latn";

const weekdayFormatter = new Intl.DateTimeFormat(LOCALE, { weekday: "long", timeZone: "Asia/Tehran" });
const timeFormatter = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Tehran" });

/** long «12 مهر 1405» · short «1405/07/12» · dayMonth «12 مهر» · month «مهر» · monthYear «مهر 1405» · weekday «شنبه 12 مهر» · datetime */
export type JalaliStyle = "long" | "short" | "dayMonth" | "month" | "monthYear" | "weekday" | "datetime";

export function toDate(value: DateLike): Date {
    return value instanceof Date ? value : new Date(value);
}

export function formatJalali(value: DateLike, style: JalaliStyle = "long"): string {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) return "";
    const { jy, jm, jd } = toJalali(d);
    const month = JALALI_MONTH_NAMES[jm - 1];
    switch (style) {
        case "short":
            return sharedFormatJalali(d, { persianDigits: false });
        case "long":
            return `${jd} ${month} ${jy}`;
        case "dayMonth":
            return `${jd} ${month}`;
        case "month":
            return month;
        case "monthYear":
            return `${month} ${jy}`;
        case "weekday":
            return `${weekdayFormatter.format(d)} ${jd} ${month}`;
        case "datetime":
            return `${jd} ${month} ${jy}، ساعت ${timeFormatter.format(d)}`;
    }
}

/** Jalali year, month (1..12) and day for a date (Tehran calendar day). */
export function jalaliParts(value: DateLike): { year: number; month: number; day: number } {
    const { jy, jm, jd } = toJalali(toDate(value));
    return { year: jy, month: jm, day: jd };
}

const DAY_MS = 86_400_000;

/** Whole days from `now` until `target`, rounded up (a partial day counts as one). Negative when past. */
export function daysUntil(target: DateLike, now: DateLike = new Date()): number {
    return Math.ceil((toDate(target).getTime() - toDate(now).getTime()) / DAY_MS);
}

export function addDays(value: DateLike, days: number): Date {
    return addCalendarDays(toDate(value), days);
}
